import { useCallback, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  AlertCircle,
  Calendar,
  Check,
  CheckCircle2,
  Clock3,
  Download,
  Eye,
  Plus,
  RefreshCw,
  Search,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Permit } from '@/components/guards/Permit'
import {
  FORM_CONTROL_FOCUS_CLASS,
  FORM_FILTER_HOVER_CLASS,
} from '@/components/ui/form-control-styles'
import { Input } from '@/components/ui/input'
import { MultiSelectFilter } from '@/components/ui/multi-select-filter'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { ListPagination } from '@/components/shared/ListPagination'
import { ListExportDialog } from '@/components/shared'
import { TruncatedTextTooltip } from '@/components/shared/TruncatedTextTooltip'
import {
  ClientNameWithProspect,
  PaymentStatusBadge,
} from '@/components/shared/StatusBadges'
import { PaymentDrawerShell } from '@/components/payments/PaymentDrawerShell'
import { PaymentCreateDrawerContent } from '@/components/payments/PaymentCreateDrawerContent'
import { PaymentDetailDrawerContent } from '@/components/payments/PaymentDetailDrawerContent'
import { UpdatePaymentStatusDialog } from '@/components/payments/UpdatePaymentStatusDialog'
import { DateRangeFilter } from '@/components/clients/tabs/DateRangeFilter'
import { ClientSelectFilter } from '@/components/billing/ClientSelectFilter'
import { useTopBarActions } from '@/contexts/TopBarActionsContext'
import { usePaymentsList, usePaymentsSummary } from '@/hooks/usePayments'
import { usePermissions } from '@/hooks/usePermissions'
import { paymentsApi } from '@/services/payments'
import { useUrlListParams } from '@/hooks/useUrlListParams'
import { formatCurrency, formatDate } from '@/lib/format'
import { cn } from '@/lib/utils'
import {
  PAYMENT_METHOD_LABELS,
  type PaymentFailureReason,
  type PaymentListQuery,
  type PaymentMethod,
  type PaymentStatus,
} from '@/types'

const ALL = '__all__'

const topBarNewPaymentClass =
  'h-10 gap-2 rounded-xl border border-[#ff5a1f] bg-[#ff5a1f] px-4 py-3 text-sm font-medium leading-none text-white hover:bg-[#e6511c]'

const STATUS_OPTIONS: { value: string; labelKey: string }[] = [
  { value: 'pending', labelKey: 'statusOptions.pending' },
  { value: 'paid', labelKey: 'statusOptions.paid' },
  { value: 'failed', labelKey: 'statusOptions.failed' },
  { value: 'refunded', labelKey: 'statusOptions.refunded' },
]

const METHOD_OPTIONS: { value: string; labelKey: string }[] = [
  { value: 'cash', labelKey: 'methodOptions.cash' },
  { value: 'cheque', labelKey: 'methodOptions.cheque' },
  { value: 'transfer', labelKey: 'methodOptions.transfer' },
  { value: 'card', labelKey: 'methodOptions.card' },
]

type Filters = {
  status?: string
  method?: string
  date_from?: string
  date_to?: string
  client_id?: string
  client_name?: string
  invoice_status?: string
}

type OpenFilter = 'date' | 'status' | 'method' | 'client'

type SummaryCardKey = 'received' | 'thisMonth' | 'awaiting' | 'remaining'

type SummaryCardConfig = {
  key: SummaryCardKey
  value: number
  count: number
  icon: typeof CheckCircle2
  border: string
  background: string
  iconColor: string
  valueColor?: string
}

function fromCsv(value: string | undefined): string[] {
  return value ? value.split(',').filter(Boolean) : []
}

function toCsv(values: string[]): string | undefined {
  return values.length ? values.join(',') : undefined
}

const filterTriggerClass = cn(
  'h-10 w-auto shrink-0 min-w-[120px] justify-between gap-2 rounded-xl border-[#e7e9ef] bg-white py-3 pl-4 pr-3 text-sm font-normal text-foreground shadow-none focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 data-[state=open]:ring-0 [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:opacity-100',
  FORM_FILTER_HOVER_CLASS
)

const summaryCardInteractionClass =
  'cursor-pointer transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-[0_12px_28px_-12px_rgba(23,26,32,0.22)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
const summaryCardActiveColor = '#ff5a1f'

const thClass =
  'h-10 border-b border-border bg-[#fbfbfc] px-3 py-2 text-[10px] font-medium uppercase tracking-normal text-[#3d434e]'

const tdClass =
  'h-16 overflow-hidden border-b border-border px-3 py-2 align-middle text-sm'

const actionBtnClass =
  'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-transparent bg-[#f4f5f8] text-foreground transition-colors group-hover:bg-[#fbfbfc] hover:border-[#ff5a1f] hover:bg-[#fff1ea] hover:text-[#ff5a1f] group-hover:hover:bg-[#fff1ea]'

const actionIconClass = 'h-4 w-4 shrink-0'

const COL = {
  clientName: 'w-[220px] min-w-[220px] text-left',
  linkedInvoice: 'w-[200px] min-w-[200px] text-left',
  amount: 'w-[120px] min-w-[120px] text-center',
  status: 'w-[140px] min-w-[140px] text-center',
  collectionDate: 'w-[120px] min-w-[120px] text-left',
  method: 'w-[140px] min-w-[140px] text-center',
  proof: 'w-[88px] min-w-[88px] text-center',
  paymentId: 'w-[148px] min-w-[148px] text-left',
  actions: 'w-[96px] min-w-[96px] text-right',
} as const

const TABLE_MIN_WIDTH = 1272
const COLUMN_COUNT = 9

function formatLocalDate(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

function SummaryCard({
  title,
  subtitle,
  value,
  icon: Icon,
  border,
  background,
  iconColor,
  valueColor,
  active,
  onClick,
}: {
  title: string
  subtitle: string
  value: string
  icon: typeof CheckCircle2
  border: string
  background: string
  iconColor: string
  valueColor?: string
  active?: boolean
  onClick?: () => void
}) {
  const content = (
    <>
      <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white shadow-[0_1px_4px_0_rgba(38,45,66,0.05)]'>
        <Icon className='h-4 w-4' style={{ color: iconColor }} />
      </div>
      <div className='flex min-w-0 flex-1 flex-col gap-1'>
        <p className='font-heading text-xs font-medium leading-[1.2] text-[#5a5a5a]'>
          {title}
        </p>
        <p
          className='font-heading text-[24px] font-semibold leading-[1.2] tracking-[-0.48px] text-foreground'
          style={valueColor ? { color: valueColor } : undefined}
        >
          {value}
        </p>
        <p className='text-xs font-medium leading-4 text-[#5a5a5a]'>
          {subtitle}
        </p>
      </div>
    </>
  )
  const className = cn(
    'flex min-w-[240px] flex-1 items-center gap-4 rounded-2xl border px-4 py-4 text-left shadow-[0_1px_4px_-1px_rgba(38,45,66,0.05),0_1px_8px_-1px_rgba(38,45,66,0.1)]',
    onClick && summaryCardInteractionClass
  )
  const style = {
    borderColor: active ? summaryCardActiveColor : border,
    background,
  }

  if (onClick) {
    return (
      <button
        type='button'
        aria-pressed={active}
        className={className}
        style={style}
        onClick={onClick}
      >
        {content}
      </button>
    )
  }

  return (
    <div className={className} style={style}>
      {content}
    </div>
  )
}

export default function PaymentsListPage() {
  const { t } = useTranslation('payments')
  const navigate = useNavigate()
  const { canEdit } = usePermissions()
  const canEditPayments = canEdit('payments')
  const [searchParams, setSearchParams] = useSearchParams()
  const {
    page,
    setPage,
    limit,
    setLimit,
    search,
    onSearchChange,
    filters,
    setFilters,
    query,
  } = useUrlListParams<Filters>({})

  const selectedStatuses = useMemo(
    () => fromCsv(filters.status),
    [filters.status]
  )
  const apiQuery = useMemo<PaymentListQuery>(() => {
    const next = { ...(query as Record<string, unknown>) }
    delete next.client_name
    delete next.status
    next.status = selectedStatuses.length ? selectedStatuses : undefined
    return next as PaymentListQuery
  }, [query, selectedStatuses])

  const { data, isLoading, isError } = usePaymentsList(apiQuery)
  const { data: summaryData } = usePaymentsSummary()
  const payments = data?.data ?? []
  const actionsGroupClass = cn(
    'ml-auto flex items-center justify-end gap-2',
    canEditPayments ? 'w-[72px]' : 'w-8'
  )
  const totalRows = data?.metadata?.total ?? payments.length
  const summary = summaryData?.data ?? null
  const [exportOpen, setExportOpen] = useState(false)
  const [openFilter, setOpenFilter] = useState<OpenFilter | null>(null)
  const [statusTarget, setStatusTarget] = useState<{
    id: string
    status: PaymentStatus
    failureReason?: PaymentFailureReason | null
  } | null>(null)

  const handleFilterOpenChange = useCallback(
    (filter: OpenFilter, open: boolean) => {
      setOpenFilter(current =>
        open ? filter : current === filter ? null : current
      )
    },
    []
  )

  const activePanel = searchParams.get('panel')
  const selectedPaymentId = searchParams.get('payment')
  const createClientId = searchParams.get('create_client_id') ?? ''
  const createJobId = searchParams.get('create_job_id') ?? ''
  const createInvoiceId = searchParams.get('create_invoice_id') ?? ''
  const createAmount = searchParams.get('create_amount') ?? ''
  const reportJob = searchParams.get('report_job') ?? ''
  const autoOpenDetailStatus = searchParams.get('detail_status') === '1'
  const currentMonthRange = useMemo(() => {
    const now = new Date()
    const start = new Date(now.getFullYear(), now.getMonth(), 1)
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0)

    return {
      date_from: formatLocalDate(start),
      date_to: formatLocalDate(end),
    }
  }, [])

  const openCreatePanel = useCallback(
    (prefill?: {
      clientId?: string
      jobId?: string
      invoiceId?: string
      amount?: string
      reportJob?: string
    }) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev)
          next.set('panel', 'create')
          next.delete('payment')
          next.delete('detail_status')
          if (prefill?.clientId) next.set('create_client_id', prefill.clientId)
          else next.delete('create_client_id')
          if (prefill?.jobId) next.set('create_job_id', prefill.jobId)
          else next.delete('create_job_id')
          if (prefill?.invoiceId)
            next.set('create_invoice_id', prefill.invoiceId)
          else next.delete('create_invoice_id')
          if (prefill?.amount) next.set('create_amount', prefill.amount)
          else next.delete('create_amount')
          if (prefill?.reportJob) next.set('report_job', prefill.reportJob)
          else next.delete('report_job')
          return next
        },
        { replace: false }
      )
    },
    [setSearchParams]
  )

  const openDetailPanel = useCallback(
    (paymentId: string, options?: { openStatus?: boolean }) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev)
          next.set('panel', 'detail')
          next.set('payment', paymentId)
          next.delete('create_client_id')
          next.delete('create_job_id')
          next.delete('create_invoice_id')
          next.delete('create_amount')
          next.delete('report_job')
          if (options?.openStatus) next.set('detail_status', '1')
          else next.delete('detail_status')
          return next
        },
        { replace: false }
      )
    },
    [setSearchParams]
  )

  const closePanel = useCallback(() => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev)
        next.delete('panel')
        next.delete('payment')
        next.delete('detail_status')
        next.delete('create_client_id')
        next.delete('create_job_id')
        next.delete('create_invoice_id')
        next.delete('create_amount')
        next.delete('report_job')
        return next
      },
      { replace: false }
    )
  }, [setSearchParams])

  const topBarActions = useMemo(
    () => (
      <Permit module='payments' action='edit'>
        <Button
          className={topBarNewPaymentClass}
          onClick={() => openCreatePanel()}
        >
          <Plus className='size-4' />
          {t('list.createButton')}
        </Button>
      </Permit>
    ),
    [openCreatePanel, t]
  )
  useTopBarActions(topBarActions)

  const cards: SummaryCardConfig[] = [
    {
      key: 'received',
      value: summary?.total_received ?? 0,
      count: summary?.total_received_count ?? 0,
      icon: CheckCircle2,
      border: '#a7f3d0',
      background: '#ecfdf5',
      iconColor: '#16a34a',
    },
    {
      key: 'thisMonth',
      value: summary?.this_month ?? 0,
      count: summary?.this_month_count ?? 0,
      icon: Calendar,
      border: '#bfdbfe',
      background: '#ebf1fe',
      iconColor: '#3271d7',
    },
    {
      key: 'awaiting',
      value: summary?.awaiting_collection ?? 0,
      count: summary?.awaiting_collection_count ?? 0,
      icon: Clock3,
      border: '#fde68a',
      background: '#fbf3d9',
      iconColor: '#d28d00',
    },
    {
      key: 'remaining',
      value: summary?.remaining_amount_due ?? 0,
      count: summary?.remaining_amount_due_count ?? 0,
      icon: AlertCircle,
      border: '#fca5a5',
      background: '#fde9e8',
      iconColor: '#e5484d',
      valueColor: '#d63b33',
    },
  ]

  const cardSubtitle = (key: SummaryCardKey, count: number) => {
    if (key === 'thisMonth' && count === 0) {
      return t('list.cards.thisMonthEmpty')
    }

    return t(`list.cards.${key}Subtext`, { count })
  }
  const cardAction = (key: SummaryCardKey) => {
    if (key === 'remaining') {
      return () =>
        setFilters({
          invoice_status: 'overdue',
          status: undefined,
          date_from: undefined,
          date_to: undefined,
        })
    }

    if (key === 'thisMonth') {
      return () =>
        setFilters({
          status: 'paid',
          date_from: currentMonthRange.date_from,
          date_to: currentMonthRange.date_to,
          invoice_status: undefined,
        })
    }

    return () =>
      setFilters({
        status: key === 'received' ? 'paid' : 'pending',
        date_from: undefined,
        date_to: undefined,
        invoice_status: undefined,
      })
  }
  const isCardActive = (key: SummaryCardKey) => {
    if (key === 'remaining') {
      return (
        filters.invoice_status === 'overdue' &&
        !filters.status &&
        !filters.date_from &&
        !filters.date_to
      )
    }
    if (key === 'thisMonth') {
      return (
        filters.status === 'paid' &&
        filters.date_from === currentMonthRange.date_from &&
        filters.date_to === currentMonthRange.date_to &&
        !filters.invoice_status
      )
    }

    return (
      filters.status === (key === 'received' ? 'paid' : 'pending') &&
      !filters.date_from &&
      !filters.date_to &&
      !filters.invoice_status
    )
  }

  return (
    <div className='-m-6 flex min-h-full flex-col gap-6 bg-[#F4F5F8] p-6'>
      <section className='grid gap-4 xl:grid-cols-4'>
        {cards.map(card => (
          <SummaryCard
            key={card.key}
            title={t(`list.cards.${card.key}`)}
            subtitle={cardSubtitle(card.key, card.count)}
            value={formatCurrency(card.value, 'EUR', 2)}
            icon={card.icon}
            border={card.border}
            background={card.background}
            iconColor={card.iconColor}
            valueColor={card.valueColor}
            active={isCardActive(card.key)}
            onClick={cardAction(card.key)}
          />
        ))}
      </section>

      <section className='flex flex-col gap-4'>
        <div className='flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between'>
          <div className='flex min-w-0 flex-1 flex-wrap items-center gap-3'>
            <div className='relative w-full max-w-[320px] shrink-0'>
              <Search className='pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-muted-foreground' />
              <Input
                value={search}
                onChange={e => onSearchChange(e.target.value)}
                placeholder={t('list.filters.searchPlaceholder')}
                maxLength={100}
                className={cn(
                  'h-10 rounded-xl border-[#e7e9ef] bg-white pl-10 pr-4 text-sm font-normal placeholder:text-[#6b7280]',
                  FORM_CONTROL_FOCUS_CLASS,
                  FORM_FILTER_HOVER_CLASS
                )}
              />
            </div>

            <DateRangeFilter
              appearance='field'
              labelKey='tabFilters.collectionDate'
              from={filters.date_from}
              to={filters.date_to}
              open={openFilter === 'date'}
              onOpenChange={open => handleFilterOpenChange('date', open)}
              triggerClassName={FORM_CONTROL_FOCUS_CLASS}
              onChange={({ date_from, date_to }) =>
                setFilters({ date_from, date_to })
              }
            />

            <MultiSelectFilter
              options={STATUS_OPTIONS.map(option => ({
                value: option.value,
                label: t(option.labelKey),
              }))}
              selected={selectedStatuses}
              onChange={values =>
                setFilters({
                  status: toCsv(values),
                  invoice_status: undefined,
                })
              }
              placeholder={t('list.filters.statusPlaceholder')}
              selectedLabel={count =>
                t('list.filters.statusSelected', { count })
              }
              clearLabel={t('list.filters.allStatuses')}
              emptyLabel={t('list.filters.statusEmpty')}
              open={openFilter === 'status'}
              onOpenChange={open => handleFilterOpenChange('status', open)}
              triggerClass={cn(filterTriggerClass, FORM_CONTROL_FOCUS_CLASS)}
            />

            <Select
              open={openFilter === 'method'}
              onOpenChange={open => handleFilterOpenChange('method', open)}
              value={filters.method ?? ALL}
              onValueChange={value =>
                setFilters({ method: value === ALL ? undefined : value })
              }
            >
              <SelectTrigger className={filterTriggerClass}>
                <SelectValue
                  placeholder={t('list.filters.methodPlaceholder')}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>
                  {t('list.filters.allMethods')}
                </SelectItem>
                {METHOD_OPTIONS.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {t(option.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <ClientSelectFilter
              preload
              value={filters.client_id}
              valueLabel={filters.client_name}
              open={openFilter === 'client'}
              onOpenChange={open => handleFilterOpenChange('client', open)}
              onChange={(id, name) =>
                setFilters({ client_id: id, client_name: name })
              }
              triggerClassName={FORM_CONTROL_FOCUS_CLASS}
              placeholder={t('list.filters.clientPlaceholder')}
              searchPlaceholder={t('list.filters.clientSearch')}
              clearLabel={t('list.filters.clientClear')}
              emptyLabel={t('list.filters.clientEmpty')}
              errorLabel={t('list.filters.clientError')}
            />
          </div>

          <Permit module='payments' action='view'>
            <Button
              variant='outline'
              className='h-10 rounded-xl border-[#e7e9ef] px-4 text-sm font-normal'
              onClick={() => setExportOpen(true)}
            >
              <Download className='mr-2 size-4' />
              {t('list.export')}
            </Button>
          </Permit>
        </div>

        <Card className='overflow-hidden rounded-2xl border-[#e7e9ef] bg-background shadow-sm'>
          <div className='overflow-x-auto'>
            <table
              className='w-full table-fixed caption-bottom text-sm'
              style={{ minWidth: TABLE_MIN_WIDTH }}
            >
              <TableHeader className='sticky top-0 z-10 bg-muted'>
                <TableRow className='border-0'>
                  <TableHead className={cn(thClass, COL.clientName)}>
                    {t('list.table.clientName')}
                  </TableHead>
                  <TableHead className={cn(thClass, COL.linkedInvoice)}>
                    {t('list.table.linkedInvoice')}
                  </TableHead>
                  <TableHead className={cn(thClass, COL.amount)}>
                    {t('list.table.amount')}
                  </TableHead>
                  <TableHead className={cn(thClass, COL.status)}>
                    {t('list.table.status')}
                  </TableHead>
                  <TableHead className={cn(thClass, COL.collectionDate)}>
                    {t('list.table.collectionDate')}
                  </TableHead>
                  <TableHead className={cn(thClass, COL.method)}>
                    {t('list.table.paymentMethod')}
                  </TableHead>
                  <TableHead className={cn(thClass, COL.proof)}>
                    {t('list.table.proof')}
                  </TableHead>
                  <TableHead className={cn(thClass, COL.paymentId)}>
                    {t('list.table.paymentId')}
                  </TableHead>
                  <TableHead className={cn(thClass, COL.actions)}>
                    <div className={actionsGroupClass}>
                      {t('list.table.actions')}
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>

              <TableBody>
                {isLoading &&
                  Array.from({ length: 6 }).map((_, index) => (
                    <TableRow key={index}>
                      <TableCell colSpan={COLUMN_COUNT} className={tdClass}>
                        <Skeleton className='h-6 w-full' />
                      </TableCell>
                    </TableRow>
                  ))}

                {isError && !isLoading && (
                  <TableRow>
                    <TableCell
                      colSpan={COLUMN_COUNT}
                      className={cn(
                        tdClass,
                        'py-10 text-center text-destructive'
                      )}
                    >
                      {t('list.loadError')}
                    </TableCell>
                  </TableRow>
                )}

                {!isLoading && !isError && payments.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={COLUMN_COUNT}
                      className={cn(
                        tdClass,
                        'py-10 text-center text-muted-foreground'
                      )}
                    >
                      {t('list.empty')}
                    </TableCell>
                  </TableRow>
                )}

                {!isLoading &&
                  !isError &&
                  payments.map(payment => {
                    const isSelected =
                      activePanel === 'detail' &&
                      selectedPaymentId === payment.id

                    return (
                      <TableRow
                        key={payment.id}
                        className={cn(
                          'group cursor-pointer transition-colors',
                          isSelected && 'bg-[#fff1ea]'
                        )}
                        onClick={() => openDetailPanel(payment.id)}
                      >
                        <TableCell
                          className={cn(
                            tdClass,
                            COL.clientName,
                            'relative before:absolute before:bottom-0 before:left-0 before:top-0 before:w-1 before:bg-primary before:opacity-0 before:transition-opacity group-hover:before:opacity-100',
                            isSelected && 'text-primary'
                          )}
                        >
                          <ClientNameWithProspect
                            name={
                              payment.client?.name ?? payment.client_name ?? '—'
                            }
                            category={payment.client?.category}
                            className={cn(
                              'max-w-full text-sm font-medium leading-[1.4] text-foreground transition-colors',
                              isSelected && 'text-primary'
                            )}
                          />
                        </TableCell>

                        <TableCell className={cn(tdClass, COL.linkedInvoice)}>
                          {payment.invoice_id && payment.invoice_number ? (
                            <button
                              type='button'
                              className='block min-w-0 max-w-full text-sm font-medium leading-[1.4] text-foreground hover:text-primary hover:underline'
                              onClick={event => {
                                event.stopPropagation()
                                navigate(`/invoices/${payment.invoice_id}`)
                              }}
                            >
                              <TruncatedTextTooltip
                                className='line-clamp-2'
                                content={payment.invoice_number}
                              >
                                {payment.invoice_number}
                              </TruncatedTextTooltip>
                            </button>
                          ) : (
                            <TruncatedTextTooltip
                              className='line-clamp-2 text-sm font-medium leading-[1.4] text-foreground'
                              content={payment.invoice_number ?? undefined}
                            >
                              {payment.invoice_number ?? '—'}
                            </TruncatedTextTooltip>
                          )}
                        </TableCell>

                        <TableCell
                          className={cn(
                            tdClass,
                            COL.amount,
                            'font-medium text-foreground'
                          )}
                        >
                          {formatCurrency(payment.amount, 'EUR', 2)}
                        </TableCell>

                        <TableCell className={cn(tdClass, COL.status)}>
                          <PaymentStatusBadge status={payment.status} />
                        </TableCell>

                        <TableCell className={cn(tdClass, COL.collectionDate)}>
                          {formatDate(payment.paid_date)}
                        </TableCell>

                        <TableCell className={cn(tdClass, COL.method)}>
                          {PAYMENT_METHOD_LABELS[
                            payment.method as PaymentMethod
                          ] ?? payment.method}
                        </TableCell>

                        <TableCell className={cn(tdClass, COL.proof)}>
                          {payment.proof_file_id ? (
                            <div className='flex justify-center'>
                              <button
                                type='button'
                                className='inline-flex h-[22px] w-[22px] items-center justify-center rounded-lg bg-[#D1FAE5] text-[#065F46]'
                                aria-label={t('detail.viewProof')}
                                title={t('detail.viewProof')}
                                onClick={event => {
                                  event.stopPropagation()
                                  openDetailPanel(payment.id)
                                }}
                              >
                                <Check className='h-4 w-4 stroke-[2.5]' />
                              </button>
                            </div>
                          ) : (
                            <span className='inline-block font-medium text-[#3d434e]'>
                              --
                            </span>
                          )}
                        </TableCell>

                        <TableCell className={cn(tdClass, COL.paymentId)}>
                          <TruncatedTextTooltip
                            className='line-clamp-2 text-sm font-medium leading-[1.4] text-foreground hover:underline'
                            content={payment.reference ?? payment.id}
                          >
                            {payment.reference ?? payment.id.slice(0, 8)}
                          </TruncatedTextTooltip>
                        </TableCell>

                        <TableCell className={cn(tdClass, COL.actions)}>
                          <div className={actionsGroupClass}>
                            <TruncatedTextTooltip
                              content={t('list.viewAction')}
                              delayDuration={0}
                              alwaysShow
                              closeOnPointerDown
                              className='inline-flex shrink-0'
                            >
                              <button
                                type='button'
                                className={actionBtnClass}
                                aria-label={t('list.viewAction')}
                                onClick={event => {
                                  event.stopPropagation()
                                  openDetailPanel(payment.id)
                                }}
                              >
                                <Eye className={actionIconClass} />
                              </button>
                            </TruncatedTextTooltip>

                            {canEditPayments && (
                              <TruncatedTextTooltip
                                content={t('list.updateStatusAction')}
                                delayDuration={0}
                                alwaysShow
                                closeOnPointerDown
                                className='inline-flex shrink-0'
                              >
                                <button
                                  type='button'
                                  className={actionBtnClass}
                                  aria-label={t('list.updateStatusAction')}
                                  onClick={event => {
                                    event.stopPropagation()
                                    setStatusTarget({
                                      id: payment.id,
                                      status: payment.status,
                                      failureReason: payment.failure_reason,
                                    })
                                  }}
                                >
                                  <RefreshCw className={actionIconClass} />
                                </button>
                              </TruncatedTextTooltip>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  })}
              </TableBody>
            </table>
          </div>

          <ListPagination
            metadata={data?.metadata}
            page={page}
            pageSize={limit}
            onPageChange={setPage}
            onPageSizeChange={setLimit}
            showSummary={false}
          />
        </Card>
      </section>

      <ListExportDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
        entityLabel='Payment'
        rowCount={totalRows}
        onExport={async format => {
          const response = await paymentsApi.export({
            ...apiQuery,
            format,
          })

          return {
            url: response.data!.url,
            filename: response.data!.filename,
          }
        }}
      />

      <PaymentDrawerShell
        open={activePanel === 'create'}
        title={t('form.drawerTitle')}
        titleClassName='text-base font-semibold tracking-normal'
        onClose={closePanel}
      >
        <PaymentCreateDrawerContent
          key={`create:${createClientId}:${createJobId}:${createInvoiceId}:${createAmount}:${reportJob}`}
          initialClientId={createClientId}
          initialJobId={createJobId}
          initialInvoiceId={createInvoiceId}
          initialAmount={createAmount}
          reportJob={reportJob}
          onCancel={closePanel}
          onRecorded={paymentId => openDetailPanel(paymentId)}
        />
      </PaymentDrawerShell>

      <PaymentDrawerShell
        open={activePanel === 'detail' && !!selectedPaymentId}
        title={t('detail.drawerTitle')}
        titleClassName='text-base font-semibold tracking-normal'
        onClose={closePanel}
      >
        {selectedPaymentId ? (
          <PaymentDetailDrawerContent
            key={`detail:${selectedPaymentId}:${autoOpenDetailStatus ? 'status' : 'view'}`}
            paymentId={selectedPaymentId}
            autoOpenStatus={autoOpenDetailStatus}
            onClose={closePanel}
          />
        ) : null}
      </PaymentDrawerShell>

      {statusTarget ? (
        <UpdatePaymentStatusDialog
          open={!!statusTarget}
          onOpenChange={open => {
            if (!open) setStatusTarget(null)
          }}
          paymentId={statusTarget.id}
          currentStatus={statusTarget.status}
          currentFailureReason={statusTarget.failureReason}
          onUpdated={() => {
            setStatusTarget(null)
          }}
        />
      ) : null}
    </div>
  )
}
